import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppLogo } from './hooks/useAppLogo';
import { API_BASE_URL } from './utils/apiConfig';
import './styles/homepage-dashboard.css';

// --- Utility Functions ---
const formatDateDisplay = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'TBD';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateRangeDisplay = (start, end) => {
  const startText = formatDateDisplay(start);
  const endText = formatDateDisplay(end);
  if (startText === 'TBD' && endText === 'TBD') return 'Schedule TBA';
  if (startText === 'TBD') return `Ends ${endText}`;
  if (endText === 'TBD') return `Starts ${startText}`;
  return `${startText} – ${endText}`;
};

const VALUE_METRICS = [
  { value: '120+', label: 'Tournaments' },
  { value: '18K+', label: 'Players Managed' },
  { value: '40%', label: 'Less Manual Work' },
  { value: '24/7', label: 'Reliability' }
];

const SUPPORT_CHANNELS = [
  { icon: '📞', label: 'Product Specialists', value: '+91 95672 03204', action: () => window.open('tel:+919567203204') },
  { icon: '💬', label: 'WhatsApp Concierge', value: '+91 95672 03204', action: () => window.open('https://wa.me/919567203204', '_blank') },
  { icon: '✉️', label: 'Email Support', value: 'hello@playlive.in', action: () => window.open('mailto:hello@playlive.in') }
];

function Homepage() {
  const navigate = useNavigate();
  const { logoUrl } = useAppLogo();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentTournaments, setCurrentTournaments] = useState([]);
  const [currentTournamentsState, setCurrentTournamentsState] = useState('loading');

  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchCurrentTournaments = async () => {
      setCurrentTournamentsState('loading');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/tournaments/public/current`);
        const data = response.data?.tournaments ?? [];
        const enriched = data.map((tournament) => ({
          ...tournament,
          displayDate: formatDateRangeDisplay(tournament.startDate, tournament.endDate),
          locationLabel: tournament.location || 'Venue TBA'
        }));
        setCurrentTournaments(enriched);
        setCurrentTournamentsState(enriched.length ? 'success' : 'empty');
      } catch (error) {
        // Silent error handling for demo
        setCurrentTournaments([]);
        setCurrentTournamentsState('error');
      }
    };
    fetchCurrentTournaments();
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowY: html.style.overflowY,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyOverflowY: body.style.overflowY,
      bodyHeight: body.style.height,
      bodyPosition: body.style.position
    };

    // Some app pages/modals set overflow hidden and forget to reset.
    // Ensure the homepage is always scrollable.
    html.style.setProperty('overflow', 'auto', 'important');
    html.style.setProperty('overflow-y', 'auto', 'important');
    html.style.setProperty('height', 'auto', 'important');
    body.style.setProperty('overflow', 'auto', 'important');
    body.style.setProperty('overflow-y', 'auto', 'important');
    body.style.setProperty('height', 'auto', 'important');
    body.style.position = 'relative';

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowY = prev.htmlOverflowY;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.height = prev.bodyHeight;
      body.style.position = prev.bodyPosition;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const livePreview = useMemo(() => currentTournaments.slice(0, 5), [currentTournaments]);

  return (
    <div className="public-home">
      <header className="public-home__header">
        <div className="public-home__container public-home__header-inner">
          <button className="public-home__brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={logoUrl || '/logo192.png'} alt="PlayLive Pro" />
            <div className="public-home__brand-text">
              <span className="public-home__brand-eyebrow">PlayLive Pro</span>
              <strong className="public-home__brand-title">Tournament Command Center</strong>
            </div>
          </button>

          <nav className="public-home__nav">
            <button onClick={() => handleScrollToSection('live')}>Live</button>
            <button onClick={() => handleScrollToSection('access')}>Access</button>
            <button onClick={() => handleScrollToSection('contact')}>Support</button>
          </nav>

          <div className="public-home__header-actions">
            <button className="secondary-btn" onClick={() => navigate('/login/super-admin')}>
              Super Admin
            </button>
            <button className="primary-btn" onClick={() => navigate('/login/tournament-admin')}>
              Tournament Console
            </button>
          </div>
        </div>
      </header>

      <main className="public-home__container public-home__main">
        <section className="overview-hero public-home__hero">
          <div className="overview-hero-text">
            <div className="overview-eyebrow">PlayLive Pro</div>
            <h2>Run tournaments like a control room.</h2>
            <p>
              A single place for tournament setup, player onboarding, auctions, and reports—built for fast operations.
            </p>

            <div className="public-home__hero-cta">
              <button className="primary-btn" onClick={() => navigate('/login/tournament-admin')}>Open Tournament Console</button>
              <button className="secondary-btn" onClick={() => navigate('/login/super-admin')}>Go to Super Admin</button>
            </div>
          </div>

          <div className="public-home__hero-logoPanel" aria-label="App logo">
            <div className="public-home__hero-logoCard">
              <img
                className="public-home__hero-logo"
                src={logoUrl || '/logo192.png'}
                alt="PlayLive Pro logo"
                loading="eager"
              />
              <div className="public-home__hero-logoText">
                <span>PlayLive Pro</span>
                <strong>Tournament Command Center</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="kpi-ribbon public-home__kpis" aria-label="Platform highlights">
          {VALUE_METRICS.map((metric) => (
            <div key={metric.label} className="kpi-card">
              <header>{metric.label}</header>
              <strong>{metric.value}</strong>
              <span>Across PlayLive-managed events</span>
            </div>
          ))}
        </section>

        <section className="content-section public-home__whatsNew" aria-label="What's new">
          <div className="section-header">
            <h3 className="section-title">What’s New</h3>
            <button className="secondary-btn" onClick={() => handleScrollToSection('live')}>
              View Live
            </button>
          </div>

          <div className="public-home__newGrid">
            <button className="public-home__newCard" onClick={() => navigate('/login/tournament-admin')}>
              <div className="public-home__newIcon">⚡</div>
              <div className="public-home__newBody">
                <strong>Faster admin access</strong>
                <span>Jump straight into Tournament Console and start managing.</span>
              </div>
            </button>

            <button className="public-home__newCard" onClick={() => navigate('/dashboard/superadmin')}>
              <div className="public-home__newIcon">📡</div>
              <div className="public-home__newBody">
                <strong>Monitoring dashboard</strong>
                <span>Track system health and activity from Super Admin.</span>
              </div>
            </button>

            <button className="public-home__newCard" onClick={() => handleScrollToSection('contact')}>
              <div className="public-home__newIcon">💬</div>
              <div className="public-home__newBody">
                <strong>One-tap support</strong>
                <span>Call or WhatsApp us at <b>+91 95672 03204</b>.</span>
              </div>
            </button>

            <button className="public-home__newCard" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="public-home__newIcon">✨</div>
              <div className="public-home__newBody">
                <strong>Fresh new homepage</strong>
                <span>Dashboard-style layout for a cleaner, modern feel.</span>
              </div>
            </button>
          </div>
        </section>

        <section id="access" className="operations-grid public-home__ops">
          <div className="content-section quick-actions-panel">
            <div className="panel-header minimal">
              <div>
                <p className="panel-eyebrow">Access</p>
                <h3>Choose your console</h3>
              </div>
            </div>
            <div className="quick-actions-grid">
              <button className="quick-action" onClick={() => navigate('/login/tournament-admin')}>
                <div className="quick-action-icon">🏆</div>
                <div className="quick-action-labels">
                  <strong>Tournament Admin</strong>
                  <span>Run registrations, auctions, teams</span>
                </div>
              </button>

              <button className="quick-action" onClick={() => navigate('/login/super-admin')}>
                <div className="quick-action-icon">🛡️</div>
                <div className="quick-action-labels">
                  <strong>Super Admin</strong>
                  <span>Global control and monitoring</span>
                </div>
              </button>
            </div>
          </div>

          <div className="content-section attention-panel">
            <div className="panel-header minimal">
              <div>
                <p className="panel-eyebrow">What you get</p>
                <h3>Operational speed</h3>
              </div>
              <div className="panel-meta">Light UI</div>
            </div>
            <div className="attention-list">
              <div className="attention-item">
                <strong>Fewer clicks to run an auction</strong>
                <span>Live budget tracking + instant roster updates.</span>
                <div className="attention-meta">
                  <span className="attention-pill info">Real-time</span>
                  <span className="attention-pill muted">Audit-friendly</span>
                </div>
              </div>
              <div className="attention-item">
                <strong>Cleaner tournament ops</strong>
                <span>Players, teams, schedules, and exports in one flow.</span>
                <div className="attention-meta">
                  <span className="attention-pill warning">Less manual work</span>
                  <span className="attention-pill muted">Role-based</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="live" className="content-section public-home__live">
          <div className="section-header">
            <h3 className="section-title">Live Tournaments</h3>
            <button className="section-action" onClick={() => navigate('/login/tournament-admin')}>
              Open Console
            </button>
          </div>

          {currentTournamentsState === 'loading' && (
            <div className="empty-state-block">Loading tournaments…</div>
          )}
          {currentTournamentsState === 'error' && (
            <div className="timeline-error">Could not load public tournaments right now.</div>
          )}
          {currentTournamentsState === 'empty' && (
            <div className="empty-state-block">No public tournaments are currently live.</div>
          )}

          {currentTournamentsState === 'success' && (
            <div className="public-home__live-list">
              {livePreview.map((t) => (
                <article key={t.code || t._id || t.name} className="launch-card">
                  <div className="launch-card-content">
                    <div className="launch-name">{t.name}</div>
                    <div className="launch-meta">
                      <span className="launch-tier-tag">{t.sport || 'Sports'}</span>
                      <span className="launch-status">{t.status || 'Upcoming'}</span>
                    </div>
                    <div className="launch-date">{t.displayDate}</div>
                    <div className="launch-year">{t.locationLabel}</div>
                  </div>
                  <button className="launch-open-btn" onClick={() => handleScrollToSection('contact')}>
                    Get help
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>

        <section id="contact" className="content-section public-home__contact">
          <div className="panel-header minimal">
            <div>
              <p className="panel-eyebrow">Support</p>
              <h3>Need help?</h3>
            </div>
          </div>

          <div className="public-home__support-grid">
            {SUPPORT_CHANNELS.map((channel) => (
              <button key={channel.label} className="public-home__support-card" onClick={channel.action}>
                <div className="public-home__support-icon">{channel.icon}</div>
                <div className="public-home__support-text">
                  <strong>{channel.label}</strong>
                  <span>{channel.value}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </main>

      <footer className="public-home__footer">
        <div className="public-home__container public-home__footer-inner">
          <span>© {new Date().getFullYear()} PlayLive Pro</span>
          <div className="public-home__footer-links">
            <button onClick={() => navigate('/login/super-admin')}>Super Admin</button>
            <button onClick={() => navigate('/login/tournament-admin')}>Tournament Console</button>
          </div>
        </div>
      </footer>

      <button
        className={`public-home__scroll-top ${showScrollTop ? 'is-visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        ↑
      </button>
    </div>
  );
}

export default Homepage;
